/* eslint-disable @nx/enforce-module-boundaries */
import { BoxMinus, BoxPlus, FolderClosed, FolderIcon } from '@mybridge/icons';
import { HStack, Stack, Text, Spinner, Center } from '@mybridge/ui';
import _ from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getSubfileNFolder, getTeamDiskDetails } from 'v4/store/actions/myDisk.Action';
import { DiskContext } from '../context';
import { FileFolderIcon } from '../my-disk/file-icon';
import { isRootDir, isTeamDir, toPascalCase } from 'v4/components/utils';

export const DiskDirectoryTree = ({ setSelectedItem }) => {
  const { rootDirs: directory, selectedFile, selectedTeam, teamDisk, refetchMyFiles, movableTeam, setMovableTeam, myFiles,  myTeamFolders,
    refetchMyTeamFolders, myTeamFoldersLoading, rootDirsLoading, type, refetchTeamDisk } = useContext(DiskContext);
  const [tree, setTree] = useState([]);
  const [selected, setSelected] = useState();
  const dispatch = useDispatch();
  
  const [isTeamMode] = useState(() => !!(selectedFile?.team_ref || type === 'team-disk'));

  useEffect(() => {
    const sourceData = isTeamMode ? myTeamFolders : directory;
    const isLoading = isTeamMode ? myTeamFoldersLoading : rootDirsLoading;

    if (sourceData && sourceData.length > 0 && tree.length === 0) {
      const dtree = _.cloneDeep(sourceData);
      setTree([...(dtree ?? [])]);
    } else if (sourceData && sourceData.length === 0 && tree.length === 0 && !isLoading) {
      setTree([]);
    }
  }, [directory, myTeamFolders, isTeamMode, tree.length, myTeamFoldersLoading, rootDirsLoading]);

  useEffect(() => {
    setSelectedItem?.(selected);
  }, [selected]);


  useEffect(() => {
    if (teamDisk && teamDisk.id !== movableTeam?.id) {
      setMovableTeam(teamDisk);
    }
  }, [teamDisk, movableTeam?.id]);

  useEffect(() => {
    if (selectedFile?.team_ref && movableTeam?.team_ref !== selectedFile?.team_ref) {
      dispatch(getTeamDiskDetails(selectedFile.team_ref)).then((resp) => {
        let td = resp?.payload;
        // FIX: getTeamDiskDetails returns paginated {results:[...]} — unwrap the first item
        if (td?.results) td = td.results[0];
        else if (Array.isArray(td)) td = td[0];
        if (td?.id) {
          setMovableTeam(td);
        }
      });
    }
  }, [selectedFile?.team_ref, movableTeam?.team_ref]);

  useEffect(() => {
    if (movableTeam?.id) {
      refetchMyTeamFolders();
    }
  }, [movableTeam?.id]);

  useEffect(() => {
    if (isTeamMode) {
      // Only refetch context team disk if we are actually in a team disk view and have an ID
      if (type === 'team-disk' && (selectedTeam?.id || teamDisk?.id)) {
        refetchTeamDisk?.();
      }
    }
  }, []);


  const getItemFromTree = (arr = [], id) => {
    if (!arr || arr.length <= 0) return;
    let found = arr?.find?.((item) => item.id === id);
    if (!found) {
      for (let item_ of arr) {
        found = getItemFromTree(item_?.items, id);
        if (found) return found;
      }
    }
    return found;
  };

  const openFolder = (id, index) => {
    const item = getItemFromTree(tree, id);
    setSelected(item);
    dispatch(getSubfileNFolder({ parent: id, team_id: item?.team_info?.id, type: 'D' })).then((response) => {
      const results = response.payload.results ?? response.payload;
      item.opened = true;
      setTree([...(tree ?? [])]);
      if (results?.length) {
        item.items = _.cloneDeep(results);
        setTree([...(tree ?? [])]);
      }
    });
  };

  const closeFolder = (id, index) => {
    const item = getItemFromTree(tree, id);
    item.items = [];
    item.opened = false;
    setTree([...(tree ?? [])]);
    setSelected(null);
  };

  const getNameByFileType = (item) => {
    return item?.type === 'TD' ? item?.team_info?.name : item.name;
  };

  const isDir = (item) => ['RD', 'TD', 'D'].includes(item.type);

  // console.log('isSelected', selectedFile, myTeamFolders);

  const renderTree = (tree, indent = 0) => {
    return tree?.map?.((item, index) => (
      <HStack
        key={index}
        style={{ paddingLeft: indent > 0 ? `${indent}px` : 0 }}
        w="full"
      >
        <Stack w="full">
          <HStack
            _hover={{ bg: 'brandGray.500' }}
            borderRadius="5"
            cursor="pointer"
            w="full"
            p={2}
            bg={selected?.id === item?.id ? 'brandGray.500' : ''}
            onClick={
              item?.type === 'F'
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                ? () => {}
                : item.opened
                ? () => closeFolder(item.id, index)
                : () => openFolder(item.id, index)
            }
          >
            {isDir(item) ? (
              <div className="icon">
                {item.opened ? <BoxMinus /> : <BoxPlus />}
              </div>
            ) : (
              ''
            )}
            <HStack>
              {isDir(item) ? (
                <>
                  {item?.opened ? (
                    <FolderIcon width={34} height={28} />
                  ) : (
                    <FolderClosed />
                  )}
                </>
              ) : (
                <FileFolderIcon file={item} width={28} height={28} />
              )}
              <Text
                truncate
                maxW={80}
                title={getNameByFileType(item)}
                className="ps-2"
                textTransform="capitalize"
              >
                {isRootDir(item)
                  ? toPascalCase(getNameByFileType(item))
                  : getNameByFileType(item)}
              </Text>
            </HStack>
          </HStack>
          {item?.items?.length ? renderTree(item.items, indent + 22) : ''}
        </Stack>
      </HStack>
    ));
  };

  if ((myTeamFoldersLoading || rootDirsLoading) && (!tree || tree.length === 0)) {
    return (
      <Center p={4}>
        <Spinner />
      </Center>
    );
  }

  return renderTree(tree);
};
